import { z } from "zod";
// Which width-specific vectors a row carries. Vision's embedding config
// decides the width (image 512|1024, fused 896|1408); `imageDim`/`fusedDim`
// stay the legacy stored values, so read these instead.
const widthFlags = {
    hasImage512: z.boolean(),
    hasImage1024: z.boolean(),
    hasFused896: z.boolean(),
    hasFused1408: z.boolean(),
};
// Vector columns intentionally OMITTED — too large for list responses.
export const productEmbeddingInfo = z.object({
    id: z.string().uuid(),
    productId: z.string().uuid(),
    productImageId: z.string().uuid().nullable(),
    faceType: z.string(),
    faceIndex: z.number().int(),
    sourceType: z.string().nullable(),
    sourceUri: z.string().nullable(),
    sourceHash: z.string().nullable(),
    modelName: z.string(),
    modelVersion: z.string(),
    ocrText: z.string().nullable(),
    imageDim: z.number().int(),
    textDim: z.number().int(),
    fusedDim: z.number().int(),
    metric: z.string(),
    isActive: z.boolean(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
    ...widthFlags,
});
export const productEmbeddingsListResponse = z.object({ items: z.array(productEmbeddingInfo) });
// Which kind of vector the search uses for the cosine NN scan. The query
// vector's width then picks the column (image 512|1024, fused 896|1408).
// The Default ('image') matches the v1 single-mode behaviour before this
// contract grew.
export const embeddingType = z.enum(["image", "text", "fused"]);
/**
 * Search input. Exactly one of productId / vector / imageUrl must be present:
 *
 *   productId — convenience: look up THIS product's vector of the requested
 *               `embeddingType` and search against it. Useful for
 *               "find similar SKUs to product X".
 *   vector    — caller supplies the query vector directly. Useful for
 *               external classifiers / robot pipelines that already
 *               produced an embedding and don't want to store it as a
 *               product image. Its length picks the column:
 *               image=512|1024, fused=896|1408, text=384.
 *   imageUrl  — server-side fetch (SSRF-validated, redirect-safe) → vision
 *               embed → search. Useful for "what does this shelf crop look
 *               like?" without polluting product_images.
 *
 * `embeddingType` picks the column the cosine search runs on; both the
 * query side (for `productId` / `imageUrl` modes) and the result-row side
 * use this column. Default 'image' so callers that only set productId
 * keep the v1 behaviour.
 */
/** Product views the search may be restricted to (maps to pe.face_type). */
const VIEW_TYPES = [
    "front",
    "back",
    "left",
    "right",
    "top",
    "bottom",
    "angled",
    "shelf_crop",
    "unknown",
];
/**
 * Optional view filter for the cosine search. Accepts a single value, a
 * comma-separated string ("front,back"), or an array (["front","back"]).
 * OMITTED → no view filter at all (search runs across every face_type). Only
 * when the caller sends it do we restrict; when sent it must name at least one
 * view. Pass multiple to search across several views at once.
 */
export const viewTypeFilter = z
    .union([z.string(), z.array(z.string())])
    .transform((v) => (Array.isArray(v) ? v : v.split(","))
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean))
    .pipe(z.array(z.enum(VIEW_TYPES)).min(1))
    .optional();
export const searchEmbeddingsRequest = z
    .object({
    productId: z.string().uuid().optional(),
    vector: z.array(z.number()).optional(),
    imageUrl: z.string().url().optional(),
    embeddingType: embeddingType.default("image"),
    // Restrict candidates to these product views (default ["front"]).
    viewType: viewTypeFilter,
    limit: z.coerce.number().int().min(1).max(100).default(20),
})
    .refine((v) => {
    const provided = [v.productId, v.vector, v.imageUrl].filter((x) => x !== undefined).length;
    return provided === 1;
}, {
    message: "exactly one of productId / vector / imageUrl is required",
});
/**
 * Full embedding row + search-result metadata. Mirrors productEmbeddingInfo's
 * fields so callers don't need a second call to `/embeddings` to render the
 * row; adds `distance` (the cosine result) and `searchedColumn` (which
 * vector column the search ran against — useful for debugging mismatched
 * embeddingType expectations).
 */
export const searchEmbeddingHit = z.object({
    // Identity
    id: z.string().uuid(),
    productId: z.string().uuid(),
    productName: z.string().nullable(),
    productArticleCode: z.string().nullable(),
    productCategoryId: z.string().uuid().nullable(),
    productCategoryName: z.string().nullable(),
    productImageId: z.string().uuid().nullable(),
    // Embedding row metadata
    faceType: z.string(),
    faceIndex: z.number().int(),
    sourceType: z.string().nullable(),
    sourceUri: z.string().nullable(),
    sourceHash: z.string().nullable(),
    modelName: z.string(),
    modelVersion: z.string(),
    ocrText: z.string().nullable(),
    imageDim: z.number().int(),
    textDim: z.number().int(),
    fusedDim: z.number().int(),
    metric: z.string(),
    isActive: z.boolean(),
    // metadata jsonb is intentionally omitted from search hits — the field
    // carries the full vision_raw response and other noisy debug payload
    // which is rarely useful at the search-consumer surface and bloats
    // the response. Fetch GET /products/:productId/embeddings if you need it.
    createdAt: z.string().datetime(),
    // Search-specific
    // Cosine similarity (1 − distance): 1 = identical, 0 = orthogonal,
    // −1 = opposite. Higher = better match. Primary ranking metric.
    similarity: z.number(),
    // Cosine distance via pgvector `<=>`: 0 = identical, 2 = opposite. Kept for
    // backward-compat / debugging; prefer `similarity`.
    distance: z.number(),
    searchedColumn: embeddingType,
    // Width of the column searched (from the query vector, or
    // EMBEDDING_ACTIVE_DIM in productId mode).
    searchedDim: z.number().int(),
    ...widthFlags,
});
export const searchEmbeddingsResponse = z.object({
    items: z.array(searchEmbeddingHit),
});
// ── Embedding job / run shape (used by per-image POST + history GET) ─────
// The wire shape is "run" terminology (operator-facing) but is backed by
// the embedding_jobs table internally — the lifecycle is queue-based and
// asynchronous. POST /images/:imageId/embed returns a freshly-queued row
// (status='queued'); the frontend polls /embedding-runs to watch it
// transition through processing → completed | failed.
export const embeddingRunInfo = z.object({
    id: z.string().uuid(),
    productImageId: z.string().uuid().nullable(),
    imageViewType: z.string().nullable(),
    imageUri: z.string().nullable(),
    triggeredByEmail: z.string().nullable(),
    status: z.enum(["queued", "processing", "completed", "failed", "cancelled"]),
    sourceType: z.enum(["image_upload_auto", "manual_per_image", "model_sweep"]),
    modelName: z.string().nullable(),
    modelVersion: z.string().nullable(),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    durationMs: z.number().int().nullable(),
    errorMessage: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const embeddingRunsListResponse = z.object({
    items: z.array(embeddingRunInfo),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
export const embeddingRunsListQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
//# sourceMappingURL=product-embeddings.schemas.js.map